import { useEffect, useState } from 'react';

/**
 * Debounce a string value (Plan 06: Garden vine search). The controlled
 * input stays immediate (responsive typing); a debounced copy feeds the
 * filter pass so the whole match scan does not run on every keystroke.
 *
 * Pinned behavior (codex LOW):
 * - Non-empty changes settle after `delayMs` (150ms for search).
 * - An empty value (clear-X or backspace-to-empty) applies IMMEDIATELY,
 *   in the SAME render, so clearing the search feels instant with no
 *   debounce lag and no one stale frame.
 * - Every new value CANCELS the prior pending timer, so a stale query can
 *   never reapply after a clear (the classic debounce race).
 *
 * "Empty" is measured on the trimmed value so a whitespace-only query
 * (which the matcher treats as empty) also resets the list instantly.
 *
 * The empty case is returned SYNCHRONOUSLY during render rather than via the
 * effect. An effect runs after commit, so deferring the reset there would
 * paint one frame of the prior (stale) query before clearing. The effect
 * only owns the timer for non-empty values and resets the internal state to
 * the empty value so the next non-empty cycle does not flash the old query.
 */
export function useDebouncedValue(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  const isEmpty = value.trim() === '';

  useEffect(() => {
    // Empty value: keep the internal state in sync so a later non-empty
    // keystroke debounces from the empty baseline, not the old query. The
    // returned value below already reflects the empty value synchronously,
    // so this set never causes a visible stale frame. Documented
    // set-state-in-effect deviation per CLAUDE.md rule #75.
    if (isEmpty) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDebounced(value);
      return;
    }

    // Non-empty: settle after the delay. Cleanup cancels the pending timer
    // on the next change or on unmount, so a stale query can never reapply.
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs, isEmpty]);

  // Empty applies instantly (this render); non-empty uses the debounced state.
  return isEmpty ? value : debounced;
}
